/**
 * Loss Runs summary strip — the 3 metric cards above the table.
 *
 * Values:
 *   1. Total Paid & Reserved          → FE sum of every non-excluded row's
 *      total (paid + reserved), live (Henry, jun-23) — see the
 *      archived-claim exclusion below.
 *   2. Loss Excess the Retention      → FE sum of max(0, total − sir) per
 *      non-excluded row.
 *   3. Loss Ratio Adjusted for Ret.   → backend aggregate sir_adjusted_loss_ratio
 *      ↳ the backend returns a RATIO (0.4303 → 43%), so it's rendered as a
 *        percent. NOTE: Andressa's Figma shows "$25,000" here (a dollar) —
 *        $ vs % is still open with Henry/Andressa (orion-rewiring-questions.md).
 *
 * Layout per Andressa's Figma (jun-16): NO left icon. The ⓘ info glyph is
 * HIDDEN for now — the tooltip definitions don't exist yet (Linda/Henry),
 * so we leave it out until the copy is ready (then re-add it top-right,
 * wrapped in atlas `Tooltip` — see premium-selection `InfoTip`).
 */
import { useFacilities } from "../../../../hooks/use-facilities";
import { useLossRuns } from "../../../../hooks/use-loss-runs";
import type { Facility } from "../../../../types/facility";
import { isExcludedFromLossRunTotals } from "../claim-archive";
import { computeTotalIncurred, displayMoney, lossExcessOf, num } from "../loss-run-calc";
import { displayPercent } from "../loss-summary-calc";

const EMPTY = "—";

/** Converts the label to a clean kebab-case slug for data-autotest. */
const slug = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

/** Stable fallback while the facilities query is still loading. */
const EMPTY_FACILITIES: Facility[] = [];

interface SummaryCardProps {
    label: string;
    value: string;
}

function SummaryCard({ label, value }: SummaryCardProps) {
    return (
        <div className="border-secondary bg-primary shadow-xs flex flex-col gap-1 rounded-xl border px-5 py-4">
            <span className="text-tertiary text-sm font-semibold">{label}</span>
            <span className="text-primary truncate text-display-xs font-semibold" data-autotest={`loss-runs-summary-cards-${slug(label)}`}>
                {value}
            </span>
        </div>
    );
}

interface LossRunsSummaryCardsProps {
    submissionId: string | undefined;
}

export function LossRunsSummaryCards({ submissionId }: LossRunsSummaryCardsProps) {
    // Same shared pubsub the table uses — refreshes as rows change.
    const { state } = useLossRuns(submissionId);
    const aggregates = state.status === "ready" ? state.aggregates : null;
    const allRows = state.status === "ready" ? state.data : [];
    // Claims on an archived facility, or archived themselves, don't count
    // (OR-1431) — the same filter the table's footer totals apply, so the
    // cards and the TOTAL row can't drift apart from each other. NOTE: the
    // backend's /loss-runs/summary (card 3 below) only drops facility-archived
    // claims — it has no concept of claim-level archiving — so that one card
    // can still disagree with these two for a claim archived on its own.
    const { state: facilitiesState } = useFacilities(submissionId);
    const facilities = facilitiesState.status === "ready" ? facilitiesState.data : EMPTY_FACILITIES;
    const rows = allRows.filter((r) => !isExcludedFromLossRunTotals(facilities, r));
    // Cards 1 & 2 sum the per-row FE figures (live, Henry jun-23).
    const totalPaidReserved = rows.reduce(
        (sum, r) => sum + computeTotalIncurred(r.paidIndemnity, r.paidExpense, r.reservedIndemnity, r.reservedExpense),
        0,
    );
    const lossExcess = rows.reduce(
        (sum, r) =>
            sum + lossExcessOf(computeTotalIncurred(r.paidIndemnity, r.paidExpense, r.reservedIndemnity, r.reservedExpense), r.sirByCommunity),
        0,
    );
    // Card 3 is a ratio from the backend (0.4303 → 43%).
    const percent = (s: string | undefined) => (s && s.trim() !== "" ? displayPercent(num(s)) : EMPTY);

    return (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <SummaryCard label="Total Paid & Reserved" value={displayMoney(totalPaidReserved)}/>
            <SummaryCard label="Loss Excess the Retention" value={displayMoney(lossExcess)}/>
            <SummaryCard label="Loss Ratio Adjusted for Retention" value={percent(aggregates?.sirAdjustedLossRatio)}/>
        </div>
    );
}
